<template>
	<main v-if="horse && horse.horse_id && !loading">
		<HeaderTab 
		:title="horse.horse_nom | multiCapitalize"
		:horse="horse"
		:presence="presence"
		:swiper_tabs="swiper_tabs"
		:icon="['fal', 'horse']"
		>
			<template #actions>
				<b-button variant="outline-danger" size="sm" class="d-md-none" @click="deleteHorse">
					<font-awesome-icon :icon="['fal', 'trash-alt']" />
				</b-button>
			</template>
		</HeaderTab>
		<div id="content">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12 col-xxl">
						<div>
							<keep-alive>
								<router-view :horse="horse"></router-view>
							</keep-alive>
						</div>
					</div>
				</div>
			</div>
		</div>

		<b-modal ref="confirmDelete" hide-footer centered>
			<template v-slot:modal-title>
				<font-awesome-icon :icon="['fal', 'horse']" class="text-primary mr-2" />
				{{ $t('horse.confirm_supprimer_jument') }}
			</template>

			<div class="mb-3">
				<div class="row align-items-center">
					<div class="col">
						<h5 class="mb-0">{{ horse.horse_nom }}</h5>
						<div v-if="horse.pedigree && horse.pedigree.pere">{{ horse.pedigree.pere }} × {{ horse.pedigree.mere }}</div>
					</div>
				</div>
			</div>

			<div class="d-flex justify-content-between">
				<b-button variant="secondary" :disabled="deleting" @click="$refs.confirmDelete.hide()">{{ $t('global.annuler') }}</b-button>
				<b-button variant="primary" :disabled="deleting" @click="confirmDeleteHorse">
					<template v-if="deleting"><b-spinner small /></template>
					<template v-else>{{ $t('horse.supprimer') }} <font-awesome-icon :icon="['fal', 'trash-alt']" /></template>
				</b-button>
			</div>
		</b-modal>
	</main>
	
	<main v-else>
		<div id="content">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12">
						<div v-if="loading">
							<LoadingSpinner />
						</div>
						<div v-else>
							<HorseNotFound />
						</div>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>


<script type="text/javascript">
import HorseMixin from "@/mixins/Horse.js"
import ActeMixin from "@/mixins/Actes.js"

export default {
	name: "HorseFiche",
	mixins: [
	ActeMixin,
	HorseMixin
	],
	data () {
		return {
			loading: true,
			horse_id: 0,
			horse: {},
			presence: null,
			has_acts: false,
			has_products: false,
			swiper_tabs: [],
			deleting: false
		}
	},
	created() {
		this.$store.commit({ type: 'updatehorseFicheReadonly', val: true })
	},
	mounted() {
		this.horse_id = this.$route.params.horse_id
		this.init_component()
	},
	methods: {
		async init_component() {
			
			Promise.all([
			this.loadHorse()
			]).then(() => {
				this.setSwiperTab(this.$route.name)
				if(this.$route.name == 'horseFiche'){
					this.$router.replace({ name: this.actual_tab })
				}
				
				if(this.swiper_tabs.filter(elem => elem.href == 'HorseFicheInfo').length == 0 && this.$route.name == 'HorseFicheInfo'){
					this.$router.replace({ name: this.swiper_tabs[0].href })
				}
				
			}).finally(() => {
				this.loading = false
			})
			
			this.getHorseActualPlace(this.horse_id)
			.then(result => {
				this.presence = result
			})
			.catch(e => {
				this.presence = null
			})
		},
		async loadHorse() {
			const horse_id = parseInt(this.horse_id)
			
			// On charge les infos du cheval (en cache cond)
			this.horse = await this.getHorseById(horse_id).catch(e => {
				console.error(e)
				return null
			})
			
			this.horse.use_dedicated_recap = true // pour monceaux
			this.has_acts = this.horse.has_acts
		},
		async checkHasProducts() {
			if(!this.isMare) {
				this.has_products = false
				return
			}
			
			const temp = await this.getHorseChildren(this.horse_id).catch(e => {
				return null
			})
			this.has_products = (temp && temp.length > 0)
		},
		setSwiperTab(tab_name = '') {
			const horseFicheTabs = this.$store.state.common.tabs.horseFiche
			
			// On retourne les onglets dans l'ordre de la config
			let tabs = horseFicheTabs.map(tab => {
				return this.all_tabs.find(t => t.href == tab)
			})
			tabs.forEach(tab => tab.active = false)
			
			// ContractMareFiche s'affiche sous l'onglet ContractMareList
			const effective_tab_name = tab_name === 'ContractMareFiche' ? 'ContractMareList' : tab_name
			
			if(effective_tab_name !== '') {
				let idx = tabs.findIndex(elem => elem.href == effective_tab_name)
				if(idx > -1) {
					tabs[idx].active = true
				}
			}
			
			if(tabs.filter(elem => elem.active == true).length == 0){
				tabs[0].active = true
			}
			
			this.swiper_tabs = tabs
		},
		deleteHorse() {
			this.$refs.confirmDelete.show()
		},
		async confirmDeleteHorse() {
			if(this.deleting) return
			this.deleting = true
			try {
				await this.hideHorseFromCA({horse_ids: this.horse_id})
			} catch (e) {
				console.error('HorseFiche::confirmDeleteHorse', e)
			}
			this.deleting = false
			this.$refs.confirmDelete.hide()
			const target = this.$store.state.userAccess.hasHorseAccess ? 'horseListe' : 'mareListe'
			this.$router.replace({ name: target }).catch(() => {})
		}
	},
	computed: {
		all_tabs() {
			return [
			{
				label: 'global.informations',
				href: 'HorseFicheInfo',
				active: true,
				icon: ['far', 'info-circle']
			},
			{
				label: 'global.informations',
				href: 'HorsePedigreePdf',
				active: true,
				icon: ['far', 'info-circle']
			},
			{
				label: 'global.actes',
				href : 'HorseFicheActes',
				hidden: !this.hasActesAccess || !this.has_acts,
				active: false,
				icon: ['fal', 'tasks']
			},
			{
				label: 'global.notes',
				href : 'HorseFicheNotes',
				active: false,
				icon: ['fal', 'tasks']
			},
			{
				label: 'horse.feed',
				href : 'HorseFeed',
				hidden: !this.hasSocialFeedAccess,
				active: false,
				icon: ['fad', 'comments-alt']
			},
			{
				label: 'monte.contract',
				href : 'ContractMareList',
				hidden: !this.hasSaillieMareAccess,
				active: false,					active_routes: ['ContractMareFiche'],						icon: ['fal', 'file-contract']
			},
			{
				label: 'horse.infos_monte',
				href : 'HorseMonteInfo',
				hidden: !this.hasMonteInfosAccess || !this.isMare || this.isFoalYearling || this.isOutOfStructure || !this.has_products,
				active: false,
				icon: ['fal', 'venus-mars']
			},
			{
				label: 'horse.production',
				href : 'HorseProduct',
				hidden: !this.isMare || !this.has_products,
				active: false,
				icon: ['fal', 'syringe']
			},
			{
				label: 'horse.production',
				href : 'HorseProductDedicated',
				hidden: !this.isMare || this.isFoalYearling || this.isOutOfStructure,
				active: false,
				icon: ['fal', 'syringe']
			},
			{
				label: 'global.perfs',
				href : 'HorsePerfs',
				hidden: !this.hasPerfsAccess || this.isFoalYearling,
				active: false,
				icon: ['fal', 'binoculars']
			},
			{
				label: 'horse.suivi_croissance',
				href : 'HorseSuiviCroissance',
				hidden: !this.hasSuiviCroissanceAccess,
				active: false,
				icon: ['far', 'chart-area']
			},
			{
				label: 'horse.caracterisation_pax',
				href : 'HorsePax',
				// hidden: !this.hasPaxAccess,
				active: false,
				icon: ['cf', 'pax']
			},
			{
				label: 'horse.mouvements',
				href : 'HorseMouvements',
				hidden: !this.hasMouvementAccess,
				active: false,
				icon: ['fal', 'route']
			},
			{
				label: 'menu.semence',
				href : 'StallionSemence',
				hidden: !this.hasSemenceAccess,
				active: false,
				icon: ['fal', 'vials'],
				dropdown: [{
					label: 'monte.semence.stocks',
					href : 'StallionStocks',
					active: false,
				},
				{
					label: 'monte.semence.commandes',
					href : 'StallionCommandes',
					active: false,
				},
				{
					label: 'monte.semence.expeditions',
					href : 'StallionExpeditions',
					active: false,
				}]
			},
			{
				label: 'horse.tiers_contacts',
				href : 'HorseTiersContact',
				hidden: !this.hasHorseTiersContactAccess,
				active: false,
				icon: ['fal', 'user-tie']
			}
			]
		},
		actual_tab() {
			const tab = this.swiper_tabs.find(st => st.active === true)
			return tab.href
		},
		isMare() {
			return this.horse.horse_sexe == "F"
		},
		hasSaillieMareAccess() {
			return (this.horse.horse_sexe == "F") && this.$store.state.userAccess.hasSaillieAccess
		},
		hasActesAccess() {
			return this.$store.state.userAccess.hasActesAccess
		},
		hasSocialFeedAccess() {
			return this.$store.state.userAccess.hasSocialFeedAccess
		},
		hasMonteInfosAccess() {
			return this.$store.state.userAccess.hasMonteInfosAccess
		},
		hasPerfsAccess() {
			return this.$store.state.userAccess.hasPerfsStatsAccess
		},
		hasSuiviCroissanceAccess() {
			return this.$store.state.userAccess.hasSuiviCroissanceAccess
		},
		hasPaxAccess() {
			return this.$store.state.userAccess.hasPaxAccess
		},
		hasMouvementAccess() {
			return this.$store.state.userAccess.hasMouvementAccess
		},
		hasSemenceAccess() {
			return this.$store.state.userAccess.hasSemenceAccess
		},
		hasHorseTiersContactAccess() {
			return this.$store.state.userAccess.hasHorseTiersContactAccess
		},
		routeId() {
			return this.$route.params.horse_id
		},
		isFoalYearling() {
			if(this.horse.horse_categories.length === 0) return false
			
			const ids_monceaux = [2, 3] // oui
			const temp = this.horse.horse_categories.find(c => ids_monceaux.indexOf(c.categorie_id) > -1)
			return !!temp
		},
		isOutOfStructure() {
			if(this.horse.horse_categories.length === 0) return false
			
			const ids_monceaux = [12]
			const temp = this.horse.horse_categories.find(c => ids_monceaux.indexOf(c.categorie_id) > -1)
			return !!temp
		}
	},
	watch: {
		routeId(val) {
			this.loading = true
			this.horse_id = val
			this.init_component()
		},
		isMare() {
			this.checkHasProducts()
		},
		'$route.name'(val) {
			this.setSwiperTab(val)
		}
	},
	components: {
		HorseNotFound: () => import('GroomyRoot/components/Horse/HorseNotFound'),
		HeaderTab: () => import('@/components/HeaderTab'),
		LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
	}
}
</script>
